import { addYears } from 'date-fns'
import { useState } from 'react'

import { Button } from '@/components/lemmala-design-system/button/button'
import { DatePickerCalendar } from '@/components/lemmala-design-system/date-picker/date-picker-calendar'
import {
  getNextMonth,
  getPreviousMonth,
} from '@/components/lemmala-design-system/date-picker/utils'
import { TABLE_DATE_FILTER_LABELS as labels } from '@/components/lemmala-design-system/table/labels'
import {
  getMonthStart,
  isSameDay,
  orderDates,
} from '@/components/lemmala-design-system/table/utils/date-range-filter'

type DateRangePanelProps = {
  initialFrom?: Date
  initialTo?: Date
  onApply: (from?: Date, to?: Date) => void
  onCancel: () => void
  maxYears?: number
}

export const DateRangePanel = ({
  initialFrom,
  initialTo,
  onApply,
  onCancel,
  maxYears,
}: DateRangePanelProps) => {
  const [draftFrom, setDraftFrom] = useState(initialFrom)
  const [draftTo, setDraftTo] = useState(initialTo)
  const [hoverDate, setHoverDate] = useState<Date | undefined>()
  const [leftMonth, setLeftMonth] = useState(getMonthStart(initialFrom || initialTo))

  const tooLong =
    maxYears !== undefined && !!draftFrom && !!draftTo && draftTo > addYears(draftFrom, maxYears)
  const canApply = !tooLong && !!draftFrom && !!draftTo

  const handleDateSelect = (date: Date) => {
    setHoverDate(undefined)
    if (!draftFrom || draftTo) {
      setDraftFrom(date)
      setDraftTo(undefined)
      setLeftMonth(getMonthStart(date))
      return
    }
    const [nextFrom, nextTo] = orderDates(draftFrom, date)
    setDraftFrom(nextFrom)
    setDraftTo(nextTo)
    setLeftMonth(getMonthStart(nextFrom))
  }

  const calendarProps = {
    onChange: handleDateSelect,
    showSelectedState: false,
    variant: 'range' as const,
    rangeStart: draftFrom,
    rangeEnd: draftTo,
    hoverDate,
    onDayMouseEnter: (date: Date) => {
      if (!draftFrom || (!draftTo && !isSameDay(date, draftFrom))) setHoverDate(date)
    },
    onDayMouseLeave: () => {
      if (!draftTo) setHoverDate(undefined)
    },
  }

  return (
    <div className='flex flex-col gap-3 px-4 pb-2 pt-4'>
      <div className='grid grid-cols-2 gap-4'>
        <div className='flex flex-col gap-2'>
          <span className='text-main-foreground-tertiary text-label-default'>{labels.from}</span>
          <DatePickerCalendar
            {...calendarProps}
            value={draftFrom}
            month={leftMonth}
            onMonthChange={(month) => setLeftMonth(getMonthStart(month))}
            containerClassName='rounded-none border-none'
            showPrevNav
            showNextNav={false}
          />
        </div>
        <div className='flex flex-col gap-2'>
          <span className='text-main-foreground-tertiary text-label-default'>{labels.to}</span>
          <DatePickerCalendar
            {...calendarProps}
            value={draftTo}
            month={getNextMonth(leftMonth)}
            onMonthChange={(month) => setLeftMonth(getMonthStart(getPreviousMonth(month)))}
            containerClassName='border-none'
            showPrevNav={false}
            showNextNav
          />
        </div>
      </div>

      <div className='flex items-center justify-end gap-3 px-1 pt-3'>
        {tooLong && (
          <span className='mr-auto text-main-foreground-danger text-body-small'>
            {labels.rangeTooLong(maxYears!)}
          </span>
        )}
        <div className='flex items-center gap-2'>
          <Button size='sm' variant='ghost' onClick={onCancel}>
            {labels.cancel}
          </Button>
          <Button
            size='sm'
            variant='default'
            disabled={!canApply}
            onClick={() => onApply(draftFrom, draftTo)}
          >
            {labels.apply}
          </Button>
        </div>
      </div>
    </div>
  )
}
